import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api, readApiErrorMessage } from './useAuth';
import toast from 'react-hot-toast';
import { normalizeStatus, type TicketStatus } from '../utils/roles';

type ApiErr = { response?: { data?: { error?: { message?: string } } } };

export interface TicketUser {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
}

export interface RecordingTicket {
  id: string;
  ticketNumber: string | null;
  branchId?: number | null;
  roomId?: number | null;
  bookingId?: number | null;
  slotStartAt?: string | null;
  bookeoEventId?: string | null;
  requestedBy: TicketUser | null;
  assignedTo: TicketUser | null;
  title: string;
  description?: string | null;
  status: TicketStatus;
  sessionId: string | null;
  notes: string;
  customerPhone?: string | null;
  customerName?: string | null;
  preferredLanguage?: string | null;
  finalVideoFilename?: string | null;
  processingError?: string | null;
  acceptedAtUtc?: string | null;
  sentAtUtc?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TicketEvent {
  id: string;
  eventType: string;
  payloadJson?: string | null;
  createdAtUtc: string;
  userId?: string | null;
}

export interface DeliveryLog {
  id: string;
  phoneE164: string;
  shortLinkCode?: string | null;
  success: boolean;
  errorDetail?: string | null;
  language?: string | null;
  createdAtUtc: string;
}

export interface CreateTicketPayload {
  branchId: number;
  roomId: number;
  title: string;
  notes?: string;
  description?: string;
  slotStartAt?: string;
  bookingId?: number;
  bookeoEventId?: string;
  customerPhone?: string;
  customerName?: string;
  preferredLanguage?: string;
}

export interface UpdateTicketPayload {
  title?: string;
  notes?: string;
  description?: string;
  slotStartAt?: string;
  bookingId?: number;
  customerPhone?: string;
  customerName?: string;
  preferredLanguage?: string;
}

export interface DeliveryLinkInfo {
  id: string;
  code: string;
  expiresAtUtc: string;
  revokedAtUtc?: string | null;
  createdAtUtc: string;
  isActive: boolean;
  shortUrl: string;
}

export const useTickets = (
  filters?: { status?: string; branchId?: number },
  options?: { poll?: boolean }
) => {
  return useQuery<{ tickets: RecordingTicket[] }>(
    ['tickets', filters],
    async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.branchId) params.append('branchId', String(filters.branchId));
      const q = params.toString();
      const { data } = await api.get(`/tickets${q ? `?${q}` : ''}`);
      return data;
    },
    {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      refetchInterval: options?.poll ? 30_000 : false,
    }
  );
};

export const useTicket = (id: string) => {
  return useQuery(
    ['ticket', id],
    async () => {
      const { data } = await api.get(`/tickets/${id}`);
      return data as {
        ticket: RecordingTicket;
        events?: TicketEvent[];
        shortUrl?: string | null;
        deliveryLinks?: DeliveryLinkInfo[];
        deliveryLogs?: DeliveryLog[];
      };
    },
    {
      enabled: !!id,
      staleTime: 15_000,
      onError: (error: unknown) => {
        const err = error as ApiErr;
        toast.error(err.response?.data?.error?.message || 'Failed to load ticket');
      },
    }
  );
};

export const useCreateTicket = () => {
  const qc = useQueryClient();
  return useMutation(
    async (payload: CreateTicketPayload) => {
      const { data } = await api.post('/tickets', payload);
      return (data?.ticket ?? data) as RecordingTicket;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(['tickets']);
        toast.success('Ticket created');
      },
      onError: (error: unknown) => {
        const err = error as ApiErr;
        toast.error(err.response?.data?.error?.message || 'Failed to create ticket');
      },
    }
  );
};

export const useUpdateTicket = () => {
  const qc = useQueryClient();
  return useMutation(
    async ({ id, data }: { id: string; data: UpdateTicketPayload }) => {
      const res = await api.put(`/tickets/${id}`, data);
      return res.data.ticket as RecordingTicket;
    },
    {
      onSuccess: (_, v) => {
        qc.invalidateQueries(['tickets']);
        qc.invalidateQueries(['ticket', v.id]);
        toast.success('Ticket updated');
      },
      onError: (error: unknown) => {
        toast.error(readApiErrorMessage(error, 'Update failed'));
      },
    }
  );
};

export const useDeleteTicket = () => {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      await api.delete(`/tickets/${id}`);
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(['tickets']);
        toast.success('Ticket deleted');
      },
    }
  );
};

export const useAcceptTicket = () => {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await api.post(`/tickets/${id}/accept`);
      return data;
    },
    {
      onSuccess: (_, id) => {
        qc.invalidateQueries(['tickets']);
        qc.invalidateQueries(['ticket', id]);
        toast.success('Ticket accepted');
      },
      onError: (error: unknown) => {
        const err = error as ApiErr;
        toast.error(err.response?.data?.error?.message || 'Accept failed');
      },
    }
  );
};

export const useReleaseTicket = () => {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await api.post(`/tickets/${id}/release`);
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(['tickets']);
        toast.success('Ticket released');
      },
    }
  );
};

export const useCancelTicket = () => {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await api.post(`/tickets/${id}/cancel`);
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(['tickets']);
        toast.success('Ticket cancelled');
      },
    }
  );
};

export const useLinkSessionToTicket = () => {
  const qc = useQueryClient();
  return useMutation(
    async ({ ticketId, sessionId }: { ticketId: string; sessionId: string }) => {
      await api.patch(`/tickets/${ticketId}/link-session`, { sessionId });
    },
    {
      onSuccess: (_, v) => {
        qc.invalidateQueries(['tickets']);
        qc.invalidateQueries(['ticket', v.ticketId]);
      },
    }
  );
};

export const useSendTicketWhatsApp = () => {
  const qc = useQueryClient();
  return useMutation(
    async ({
      id,
      phone,
      language,
      roomName,
    }: {
      id: string;
      phone: string;
      language?: string;
      roomName?: string;
    }) => {
      const { data } = await api.post(`/tickets/${id}/send-whatsapp`, { phone, language, roomName });
      return data;
    },
    {
      onSuccess: (_, v) => {
        qc.invalidateQueries(['tickets']);
        qc.invalidateQueries(['ticket', v.id]);
        toast.success('Video sent via WhatsApp');
      },
      onError: (error: unknown) => {
        toast.error(readApiErrorMessage(error, 'Send failed'));
      },
    }
  );
};

export const useRevokeDeliveryLink = () => {
  const qc = useQueryClient();
  return useMutation(
    async (ticketId: string) => {
      const { data } = await api.post(`/tickets/${ticketId}/delivery-link/revoke`);
      return data;
    },
    {
      onSuccess: (_, ticketId) => {
        qc.invalidateQueries(['ticket', ticketId]);
        qc.invalidateQueries(['tickets']);
        toast.success('Delivery link revoked');
      },
      onError: (error: unknown) => {
        toast.error(readApiErrorMessage(error, 'Revoke failed'));
      },
    }
  );
};

export const useRegenerateDeliveryLink = () => {
  const qc = useQueryClient();
  return useMutation(
    async (ticketId: string) => {
      const { data } = await api.post(`/tickets/${ticketId}/delivery-link/regenerate`);
      return data as { shortUrl?: string };
    },
    {
      onSuccess: (data, ticketId) => {
        qc.invalidateQueries(['ticket', ticketId]);
        qc.invalidateQueries(['tickets']);
        toast.success(data?.shortUrl ? 'New delivery link created' : 'Link regenerated');
      },
      onError: (error: unknown) => {
        toast.error(readApiErrorMessage(error, 'Regenerate failed'));
      },
    }
  );
};

export function canEditTicket(status: string): boolean {
  const s = normalizeStatus(status);
  return s !== 'recording' && s !== 'processing' && s !== 'sent' && s !== 'cancelled';
}
